// NassauCard — live standings for a Nassau side game.
//
// Read-only. Fetches get_nassau_state; renders:
//   • Three segment rows: Front / Back / Overall — each shows the
//     match status ("A 2↑ thru 5", "AS", "3 & 2", winner_side),
//     the bet_units display, and units_won distribution.
//   • Sides A / B roster with badges.
//   • Per-hole result grid for the current segment view (Front by
//     default, tap Back / Overall to swap).
//
// Compute is server-side (db.js get_nassau_state) — this is a pure
// display component. Refresh via autoRefreshMs poll (defaults to 5s
// when rendered inside GamePage).

import { useCallback, useEffect, useState } from 'react'
import { db } from '../lib/db'

export default function NassauCard({ game_id, autoRefreshMs = 0 }) {
  const [state, setState] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [tab, setTab] = useState('front')

  // Load silently on interval refreshes — only flip the loading flag
  // on the FIRST fetch. Prior version toggled loading on every refresh
  // and produced a visible "Loading…" flash every 5s.
  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true)
    try {
      const { data } = await db('get_nassau_state', { game_id })
      setState(data)
      setError(data?.error || null)
    } catch (e) {
      setError(e.message || 'Nassau compute failed')
    }
    if (!silent) setLoading(false)
  }, [game_id])

  useEffect(() => { load() }, [load])
  useEffect(() => {
    if (!autoRefreshMs) return
    const t = setInterval(() => load({ silent: true }), autoRefreshMs)
    return () => clearInterval(t)
  }, [autoRefreshMs, load])

  if (loading && !state) return <p className="text-muted text-sm">Loading Nassau…</p>
  if (error && !state) return <p style={{ color: 'var(--red)' }}>{error}</p>
  if (!state) return null

  const { participants = [], sides, segments = {}, totals = {} } = state
  const nameFor = (pid) => participants.find(p => p.player_id === pid)?.name || pid.slice(0, 6)

  // Selectable segments — only include those with data.
  const segmentTabs = [
    { key: 'front',   label: 'Front 9' },
    { key: 'back',    label: 'Back 9' },
    { key: 'overall', label: 'Overall' },
  ].filter(s => segments[s.key])
  const activeSeg = segments[tab] || segments[segmentTabs[0]?.key]

  return (
    <div>
      {/* Segment summary strip */}
      <div style={{ display: 'grid', gap: 6, marginBottom: 12 }}>
        {segmentTabs.map(s => {
          const seg = segments[s.key]
          if (!seg) return null
          const status = describeSegment(seg)
          const isActive = tab === s.key
          return (
            <button
              key={s.key}
              onClick={() => setTab(s.key)}
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr auto auto',
                gap: 10, alignItems: 'center',
                padding: '10px 12px', borderRadius: 6,
                background: isActive ? 'rgba(212,185,122,0.12)' : 'var(--green-dark)',
                border: `1px solid ${isActive ? 'var(--gold)' : 'var(--green-mid)'}`,
                color: 'var(--cream)', textAlign: 'left', cursor: 'pointer',
              }}
            >
              <span style={{ fontWeight: 600 }}>{s.label}</span>
              <span style={{
                fontFamily: 'var(--font-mono)',
                fontSize: '0.85rem',
                color: seg.winner_side
                  ? seg.winner_side === 'A' ? 'var(--gold)' : 'var(--blue-birdie)'
                  : 'var(--gray-300)',
              }}>{status}</span>
              <span style={{ fontSize: '0.7rem', color: 'var(--gray-400)' }}>
                {seg.bet_units ? `$${seg.bet_units}` : '—'}
              </span>
            </button>
          )
        })}
      </div>

      {/* Sides roster */}
      {sides && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
          <SideBadge side="A" ids={sides.A || []} nameFor={nameFor} totals={totals} />
          <SideBadge side="B" ids={sides.B || []} nameFor={nameFor} totals={totals} />
        </div>
      )}

      {/* Per-hole grid for the active segment */}
      {activeSeg?.holes?.length > 0 && (
        <div>
          <p style={{ fontSize: '0.7rem', color: 'var(--gray-300)', margin: '0 0 6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Hole-by-hole
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(9, 1fr)', gap: 3 }}>
            {activeSeg.holes.map((h, i) => {
              const bg = h.winner_side === 'A' ? 'var(--gold)'
                : h.winner_side === 'B' ? 'var(--green-mid)'
                : 'var(--green-deep)'
              const fg = h.winner_side === 'A' ? 'var(--green-deep)'
                : h.winner_side === 'B' ? 'var(--cream)'
                : 'var(--gray-400)'
              return (
                <div
                  key={i}
                  title={`Hole ${h.hole}${h.par ? ` · par ${h.par}` : ''}: A ${h.team_scores.A} vs B ${h.team_scores.B}${h.winner_side ? ` (${h.winner_side})` : ' halve'}`}
                  style={{
                    padding: '4px 0', borderRadius: 4,
                    background: bg, color: fg,
                    textAlign: 'center',
                    fontFamily: 'var(--font-mono)', fontSize: '0.68rem', fontWeight: 700,
                    border: '1px solid var(--green-mid)',
                  }}
                >
                  {h.hole}
                </div>
              )
            })}
          </div>
          <p style={{ fontSize: '0.65rem', color: 'var(--gray-500)', margin: '6px 0 0' }}>
            Gold = A wins the hole · dark = B wins · empty = halve
          </p>
        </div>
      )}
    </div>
  )
}

function SideBadge({ side, ids, nameFor, totals }) {
  const units = ids.reduce((s, pid) => s + (Number(totals[pid]?.units) || 0), 0)
  const color = side === 'A' ? 'var(--gold)' : 'var(--green-mid)'
  const fg = side === 'A' ? 'var(--green-deep)' : 'var(--cream)'
  return (
    <div style={{
      padding: 10, borderRadius: 6,
      background: 'var(--green-dark)',
      border: `1px solid ${color}`,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <span style={{
          padding: '2px 8px', borderRadius: 999,
          background: color, color: fg,
          fontSize: '0.7rem', fontWeight: 700,
        }}>Side {side}</span>
        {units !== 0 && (
          <span style={{
            fontFamily: 'var(--font-mono)',
            fontSize: '0.78rem', fontWeight: 700,
            color: units > 0 ? 'var(--blue-birdie)' : 'var(--red)',
          }}>{units > 0 ? '+' : ''}{units.toFixed(2)}</span>
        )}
      </div>
      <div style={{ fontSize: '0.72rem', color: 'var(--cream)', lineHeight: 1.4 }}>
        {ids.map(pid => nameFor(pid)).join(', ') || '—'}
      </div>
    </div>
  )
}

// Turn a segment result into a status label — "A 2↑", "AS thru 5",
// "A 3 & 2" (closeout), or "A wins" (final walk).
function describeSegment(seg) {
  if (!seg) return '—'
  if (seg.closed_out_on_hole && seg.winner_side) {
    const lead = Math.abs(seg.final_state)
    const rem = seg.total_holes - seg.closed_out_on_hole
    return `${seg.winner_side} ${lead} & ${rem}`
  }
  const played = seg.holes?.length || 0
  if (played === 0) return '—'
  if (seg.final_state === 0) return `AS thru ${played}`
  const leader = seg.final_state > 0 ? 'A' : 'B'
  const lead = Math.abs(seg.final_state)
  if (played === seg.total_holes) return `${leader} ${lead} up · final`
  return `${leader} ${lead}↑ thru ${played}`
}
